/**
 * Manifest nudges on `dw/screen/{screen_id}/manifest` (decision-log D37 and D186;
 * technical-design §11.3). The recompile worker publishes `{"version": N}` at QoS 1
 * and never retained; the native shell subscribes for its own screen and forwards the
 * payload over the JS bridge (`ShellBridge.onManifestNudge`, dwf-032).
 *
 * This is the sub-second step of the under-five-minutes promise (dwf-030 §2). It is
 * never a dependency: with no broker, no shell, or a dropped message, the poll at
 * `poll_interval_seconds` finds the same manifest at most one interval later
 * (architecture-v2 §4.3), so everything here may fail without a screen noticing.
 */

import type { Logger } from './log.js';
import type { ShellBridge } from './shell.js';

/**
 * The version out of a nudge payload, or `null` when the message does not carry a
 * readable one. `null` is not an error the caller may swallow: aws-infrastructure §5.1
 * makes the payload "a signal, never data", so an unreadable message is still the
 * signal and still fetches — it just cannot be compared against what we already have.
 */
export function parseManifestNudgeVersion(payload: unknown): number | null {
  if (typeof payload === 'string') {
    try {
      return parseManifestNudgeVersion(JSON.parse(payload) as unknown);
    } catch {
      return null;
    }
  }
  if (typeof payload !== 'object' || payload === null) return null;
  const version = (payload as Record<string, unknown>)['version'];
  if (typeof version !== 'number' || !Number.isSafeInteger(version) || version <= 0) {
    return null;
  }
  return version;
}

export type ManifestNudgeOutcome =
  /** A fetch ran for this nudge. */
  | 'synced'
  /** A fetch ran and threw; the poll is the fallback. */
  | 'sync_failed'
  /** We already hold this version or a newer one, so there is nothing to fetch. */
  | 'already_current'
  /** A fetch was in flight; this nudge folded into the one that follows it. */
  | 'coalesced';

/** The slice of the player a nudge touches, so this module does not depend on all of it. */
export interface NudgeablePlayer {
  activeManifestVersion(): number | null;
  pendingManifestVersion(): number | null;
  syncManifest(): Promise<string>;
}

export interface ManifestNudgeHandlerOptions {
  readonly sync: () => Promise<string>;
  /** The newest version the player already holds, active or pending; `null` at boot. */
  readonly knownVersion: () => number | null;
  readonly logger: Logger;
}

/**
 * One nudge in, at most one manifest fetch out.
 *
 * A burst of nudges for the same screen — a bulk recompile, a redelivered publish —
 * must not become a burst of fetches, so a nudge that arrives while a fetch is in
 * flight folds into a single follow-up fetch after it. A nudge for a version the
 * player already holds does not fetch at all.
 */
export function createManifestNudgeHandler(
  options: ManifestNudgeHandlerOptions,
): (payload: unknown) => Promise<ManifestNudgeOutcome> {
  let inFlight: Promise<boolean> | null = null;
  let again = false;

  const pump = async (): Promise<boolean> => {
    let ok = true;
    do {
      again = false;
      try {
        const outcome = await options.sync();
        options.logger.info('manifest.nudge_synced', { outcome });
      } catch (error) {
        // The nudge is an optimisation; a failed fetch leaves the poll to do it. Loud,
        // because a nudge path that never syncs looks exactly like one that never fires.
        ok = false;
        options.logger.warn('manifest.nudge_sync_failed', {
          reason: error instanceof Error ? error.message : String(error),
        });
      }
    } while (again);
    return ok;
  };

  return async (payload: unknown): Promise<ManifestNudgeOutcome> => {
    const version = parseManifestNudgeVersion(payload);
    if (version === null) {
      options.logger.warn('manifest.nudge_unreadable', { payload_type: typeof payload });
    }

    const known = options.knownVersion();
    if (version !== null && known !== null && version <= known) {
      options.logger.debug('manifest.nudge_ignored', { version, known });
      return 'already_current';
    }

    if (inFlight !== null) {
      again = true;
      options.logger.debug('manifest.nudge_coalesced', { version });
      return 'coalesced';
    }

    const run = pump();
    inFlight = run;
    try {
      return (await run) ? 'synced' : 'sync_failed';
    } finally {
      inFlight = null;
    }
  };
}

export interface AttachManifestNudgeOptions {
  readonly bridge: ShellBridge;
  readonly player: NudgeablePlayer;
  readonly logger: Logger;
}

/**
 * Subscribes the player to the shell's nudge channel. Returns whether there is one:
 * a browser tab, the dev harness and the preview have no shell, and a shell older than
 * the method has no nudge either. Both cases are the poll, and both say so rather than
 * leaving a dead channel that looks wired.
 */
export function attachManifestNudge(options: AttachManifestNudgeOptions): boolean {
  if (options.bridge.onManifestNudge === undefined) {
    options.logger.info('manifest.nudge_unavailable', { reason: 'no shell bridge method' });
    return false;
  }

  const handle = createManifestNudgeHandler({
    sync: () => options.player.syncManifest(),
    knownVersion: () =>
      newest(options.player.activeManifestVersion(), options.player.pendingManifestVersion()),
    logger: options.logger,
  });

  options.bridge.onManifestNudge((payload: unknown) => {
    void handle(payload);
  });
  return true;
}

function newest(active: number | null, pending: number | null): number | null {
  if (active === null) return pending;
  if (pending === null) return active;
  return Math.max(active, pending);
}
